import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useApi from "../../hooks/use-api";
import { useFormik } from "formik";
import Input from "../../components/Input";
import Sidebar from "../../components/Sidebar/Sidebar";
import { SidebarItemProps } from "../../components/Sidebar/SidebarItem";
import AccountTopBar from "../../components/AccountTopBar/AccountTopBar";
import { UserType } from "../../utils/props";

const UserProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const currentType = (localStorage.getItem('type') ?? '').replace(/['"]+/g, '');
  const isAdminUser = currentType === UserType.Admin.toString();
  const adminFirstName = localStorage.getItem('first_name')?.replace(/['"]+/g, '') ?? '';
  const adminLastName = localStorage.getItem('last_name')?.replace(/['"]+/g, '') ?? '';
  const navigate = useNavigate();
  const [userProfileTag, setUserProfileTag] = useState<number | undefined>(undefined);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);

  const getCustomLinkPreview = (value: string) => {
    const customLink = (value ?? '').trim();
    if (!customLink) {
      return '';
    }

    const hasProtocol = /^https?:\/\//i.test(customLink);
    return hasProtocol ? customLink : `https://${customLink}`;
  }

  // Only admins can access this page.
  useEffect(() => {
    if (!isAdminUser) {
      navigate("/personal-information");
    }
  }, [navigate, isAdminUser]);

  const viewUserApi = useApi({
    path: `/company/${id}`,
    callback: (data) => {
      userForm.setFieldValue('firstName', data.first_name ?? '');
      userForm.setFieldValue('lastName', data.last_name ?? '');
      userForm.setFieldValue('companyName', data.company_name ?? '');
      userForm.setFieldValue('fileId', data.file_id ?? '');
      userForm.setFieldValue('phone', data.phone ?? '');
      userForm.setFieldValue('mail', data.mail ?? '');
      userForm.setFieldValue('customUrl', data.custom_url ?? '');
      setUserType(data.type);
    }
  });

  const [userType, setUserType] = useState<number | null>(null);

  useEffect(() => {
    if (id && isAdminUser) viewUserApi.get();
  }, [id, isAdminUser]);

  const userForm = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      phone: '',
      fileId: '',
      mail: '',
      customUrl: '',
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload: any = {
        first_name: values.firstName,
        last_name: values.lastName,
        file_id: values.fileId,
        phone: values.phone,
        mail: values.mail,
      };
      if (userType !== UserType.Admin) {
        payload.company_name = values.companyName;
        payload.custom_url = values.customUrl;
      }
      try {
        await updateUserApi.put(payload);
        viewUserApi.get();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    },
  });

  const updateUserApi = useApi({
    path: `/company/${id}`
  });

  const updatePasswordApi = useApi({
    path: `/user/${id}/password`
  });

  const passwordForm = useFormik({
    initialValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    validate: values => {
      const errors: { newPassword?: string; confirmNewPassword?: string } = {};
      if (!values.newPassword) {
        errors.newPassword = 'This field is required';
      }
      if (!values.confirmNewPassword) {
        errors.confirmNewPassword = 'This field is required';
      } else if (values.confirmNewPassword !== values.newPassword) {
        errors.confirmNewPassword = 'Passwords must match';
      }
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        await updatePasswordApi.post({ new_password: values.newPassword });
        resetForm();
        setShowPasswordSuccess(true);
        setTimeout(() => setShowPasswordSuccess(false), 3000);
      } catch (error) {
        setShowPasswordError(true);
        setTimeout(() => setShowPasswordError(false), 3000);
      }
    },
  });

  const sidebarItems: SidebarItemProps[] = [
    { label: 'User Information', href: '#user-information' },
    { label: 'User Documents', href: `/user-documents/${id}` },
  ];

  return (
    <>
      <AccountTopBar showNav={false} />
      <div className="pd:mx-0 min-h-screen w-full bg-white px-5 md:flex lg:px-[190px]">
      <div className="flex-shrink-0 bg-white md:min-w-[260px]">
        <Sidebar
          userName={adminFirstName + ' ' + adminLastName}
          items={sidebarItems}
          tagId={userProfileTag ?? 0}
        />
      </div>
      <div className="flex flex-1 flex-col items-start py-12 md:ml-16">
        <div className="w-full">
          <h2 id="user-information" className="mb-10 mt-5 text-2xl font-bold">
            Edit User Information
          </h2>
          {showSuccess && (
            <div className="flex items-center gap-2 bg-green-100 px-4 py-3 rounded mb-4">
              <span>Information updated!</span>
            </div>
          )}
          {showError && (
            <div className="flex items-center gap-2 bg-red-100 px-4 py-3 rounded mb-4">
              <span>Failed to update information. Please try again.</span>
            </div>
          )}
          <form
            className="mb-12 grid grid-cols-2 gap-x-6 gap-y-4"
            onSubmit={userForm.handleSubmit}
          >
            <Input
              {...userForm.getFieldProps('firstName')}
              title={'First Name *'}
              error={userForm.touched.firstName ? (userForm.errors.firstName ?? '') : ''}
            />
            <Input
              {...userForm.getFieldProps('lastName')}
              title={'Last Name *'}
              error={userForm.touched.lastName ? (userForm.errors.lastName ?? '') : ''}
            />
            {userType !== UserType.Admin && (
              <Input
                className="col-span-2"
                {...userForm.getFieldProps('companyName')}
                title={'Company Name'}
                error={userForm.touched.companyName ? (userForm.errors.companyName ?? '') : ''}
              />
            )}
            <Input
              {...userForm.getFieldProps('phone')}
              title={'Phone *'}
              error={userForm.touched.phone ? (userForm.errors.phone ?? '') : ''}
            />
            <Input
              {...userForm.getFieldProps('fileId')}
              title={'File ID'}
              error={userForm.touched.fileId ? ((userForm.errors as any).fileId ?? '') : ''}
            />
            <Input
              className="col-span-2"
              {...userForm.getFieldProps('mail')}
              title={'Email *'}
              error={userForm.touched.mail ? (userForm.errors.mail ?? '') : ''}
            />
            {userType !== UserType.Admin && (
              <Input
                className="col-span-2"
                {...userForm.getFieldProps('customUrl')}
                title={'Custom Link (opens in a new tab)'}
                error={userForm.touched.customUrl ? ((userForm.errors as any).customUrl ?? '') : ''}
              />
            )}
            {userType !== UserType.Admin && getCustomLinkPreview(userForm.values.customUrl) && (
              <div className="col-span-2 flex items-center gap-2">
                <span className="text-sm text-gray-700">Custom Link Preview</span>
                <a
                  href={getCustomLinkPreview(userForm.values.customUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  {getCustomLinkPreview(userForm.values.customUrl)}
                </a>
              </div>
            )}
            <div className="col-span-2 mt-2 flex justify-start">
              <button
                type="submit"
                className="group relative border-2 border-blue-600 bg-white px-8 py-3 font-bold uppercase tracking-wide text-blue-600 shadow-none transition hover:bg-blue-600 hover:text-white"
                style={{
                  transform: 'skew(-20deg)',
                  borderRadius: 0,
                }}
              >
                <span
                  className="block"
                  style={{
                    transform: 'skew(20deg)',
                  }}
                >
                  Update Information
                </span>
              </button>
            </div>
          </form>
          <h2 className="mb-10 mt-5 text-2xl font-bold">
            Update Password
          </h2>
          {showPasswordSuccess && (
            <div className="flex items-center gap-2 bg-green-100 px-4 py-3 rounded mb-4">
              <span>Password updated!</span>
            </div>
          )}
          {showPasswordError && (
            <div className="flex items-center gap-2 bg-red-100 px-4 py-3 rounded mb-4">
              <span>Failed to update password. Please try again.</span>
            </div>
          )}
          <form
            className="mb-12 grid grid-cols-2 gap-x-6 gap-y-4"
            onSubmit={passwordForm.handleSubmit}
          >
            <Input
              className="col-span-2"
              {...passwordForm.getFieldProps('newPassword')}
              type="password"
              title={'New Password *'}
              error={passwordForm.touched.newPassword ? (passwordForm.errors.newPassword ?? '') : ''}
            />
            <Input
              className="col-span-2"
              {...passwordForm.getFieldProps('confirmNewPassword')}
              type="password"
              title={'Confirm New Password *'}
              error={passwordForm.touched.confirmNewPassword ? (passwordForm.errors.confirmNewPassword ?? '') : ''}
            />
            <div className="col-span-2 mt-2 flex justify-start">
              <button
                type="submit"
                className="group relative border-2 border-blue-600 bg-white px-8 py-3 font-bold uppercase tracking-wide text-blue-600 shadow-none transition hover:bg-blue-600 hover:text-white"
                style={{
                  transform: 'skew(-20deg)',
                  borderRadius: 0,
                }}
              >
                <span
                  className="block"
                  style={{
                    transform: 'skew(20deg)',
                  }}
                >
                  Update Password
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  );
};

export default UserProfile;